import { useContext } from 'react';
import Link from 'next/link';
import { Loader, SvgIcon, Typography, formatUrl } from '@ascd/witsby-components';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Grid } from '@mui/material';
import get from 'lodash/get';
import { DateTime } from 'luxon';
import { AppContext } from '@contexts/appContext';
import { ISubscriptionInfo } from '@types';

interface IAdminDashboardSubscriptionInfo {
  loading: boolean;
  data: ISubscriptionInfo;
}

const AdminDashboardSubscriptionInfo = ({ loading, data }: IAdminDashboardSubscriptionInfo) => {
  const {
    state: { currentInstitution },
  } = useContext(AppContext);
  let isAccountAboutToExpire = false;
  const expiryDate = get(currentInstitution, 'subscriptionInfo.expiryDate', '');
  if (expiryDate) {
    const isTrial = currentInstitution.status === 'TRIAL';
    const tempExpiryDate = DateTime.fromJSDate(new Date(expiryDate))
      .minus({ days: isTrial ? 6 : 31 })
      .endOf('day');
    const currentDate = DateTime.fromJSDate(new Date()).endOf('day');
    isAccountAboutToExpire = currentDate > tempExpiryDate;
  }

  const getContent = () => {
    switch (currentInstitution.status) {
      case 'ACTIVE':
        return 'Your Witsby subscription will soon expire, and you will no longer have access. Please contact your Regional Partnership Manager about renewing your subscription to continue using Witsby for your professional learning needs.';
      case 'TRIAL':
        return 'Your Witsby trial subscription will soon expire, and you will no longer have access. Please contact your Regional Partnership Manager about purchasing a subscription to continue using Witsby for your professional learning needs.';
      default:
        return '';
    }
  };

  return (
    <Accordion
      {...(isAccountAboutToExpire && {
        expanded: true,
      })}
      sx={{
        '&': {
          px: 2,
          height: '100%',
          boxShadow: '0px 3px 8px 2px rgba(0, 0, 0, 0.14)',
          borderRadius: (theme) => `${theme.border.radius.LARGE} !important`,
          '&:before': {
            display: 'none',
          },
        },
        '& .MuiButtonBase-root': {
          p: 0,
          minHeight: 'auto',
        },
        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
          m: 0,
        },
        '& .MuiAccordionDetails-root': {
          p: 0,
        },
      }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Typography
            tag="h2"
            text={data?.title || 'My Subscription'}
            sx={(theme) => ({
              my: 2,
              lineHeight: 'auto',
              color: theme.palette.grey[200],
              fontSize: theme.font.size.REGULAR,
              fontWeight: theme.font.weight.BOLD,
            })}
          />
          {/* {!loading && (
            <Box
              sx={(theme) => ({
                mr: 1,
                wordBreak: 'break-all',
                color: theme.palette.grey[200],
                fontSize: theme.font.size.SMALL,
                span: {
                  fontWeight: theme.font.weight.SEMI_BOLD,
                },
              })}>
              <span>Expiring: </span>
              {DateTime.fromJSDate(new Date(data?.expiringOn))?.toFormat(`''LLLL' 'dd', 'yyyy''`)}
            </Box>
          )} */}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Loader isFullScreen />
        ) : (
          <Grid
            container
            sx={(theme) => ({
              mb: 1,
              wordBreak: 'break-all',
              color: theme.palette.grey[200],
              fontSize: theme.font.size.SMALL,
            })}>
            {isAccountAboutToExpire && (
              <Grid item xs={12} md={12} sx={{ my: 2 }}>
                <Alert
                  severity="warning"
                  sx={{ fontSize: (theme) => theme.font.size.SMALL, wordBreak: 'break-word' }}>
                  {getContent()}
                </Alert>
              </Grid>
            )}
            {data?.ascdContactInfo?.map((info) => (
              <Grid
                item
                key={info?.title}
                xs={12}
                md={6}
                sx={{
                  my: 2,
                  '& .support-icon-wrapper': {
                    display: 'flex',
                    cursor: 'pointer',
                    alignItems: 'center',
                    svg: {
                      mr: 0.5,
                      fontSize: (theme) => theme.font.size.X_LARGE,
                    },
                  },
                }}>
                <Box
                  sx={(theme) => ({
                    fontSize: '1.1rem',
                    fontWeight: theme.font.weight.SEMI_BOLD,
                  })}>
                  {info?.firstName} {info?.lastName}
                </Box>
                <Box sx={{ fontSize: '0.95rem', mb: 0.5 }}>{info?.title}</Box>
                {info?.email && (
                  <Link href={`mailto:${info.email}`}>
                    <Box className="support-icon-wrapper">
                      <SvgIcon icon="EMAIL" viewBox="0 0 22 16" />
                      <u>{info.email}</u>
                    </Box>
                  </Link>
                )}
                {info?.phone && (
                  <Link href={`tel:${info.phone}`}>
                    <Box className="support-icon-wrapper">
                      <SvgIcon icon="PHONE" viewBox="0 0 24 24" />
                      {info.phone}
                    </Box>
                  </Link>
                )}
                {info?.url && (
                  <Link href={formatUrl(info.url)} target="_blank" rel="noopener noreferrer">
                    <Box className="support-icon-wrapper">
                      <SvgIcon icon="LINK" />
                      {info.url}
                    </Box>
                  </Link>
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AdminDashboardSubscriptionInfo;
